import { useEffect, useState } from "react";
import {
  CircleNotch,
  BookOpen,
  Plus,
  Pencil,
  Trash,
  X,
  MagnifyingGlass,
  Tag,
  CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CATEGORIES = ["general", "hallmarking", "purity", "pricing", "resale", "stones", "bridal", "shopping", "legal", "care", "investment", "design", "insurance", "rates"];

export default function AdminKnowledge() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [categoriesUsed, setCategoriesUsed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [editing, setEditing] = useState(null); // null=closed, {} = new, {id,...} = edit

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (category) params.set("category", category);
      params.set("limit", "200");
      const r = await api.get(`/admin/knowledge?${params.toString()}`);
      setEntries(r.data.entries || []);
      setTotal(r.data.total || 0);
      setCategoriesUsed(r.data.categories || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load entries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, q || category ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category]);

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.question.slice(0, 60)}..."?`)) return;
    try {
      await api.delete(`/admin/knowledge/${e.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-knowledge-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold flex items-center gap-2">
            <BookOpen size={14} weight="duotone" /> Knowledge base
          </p>
          <h1 className="font-display text-3xl sm:text-4xl mt-2 text-[#0A1128]">
            Jewellery knowledge
          </h1>
          <p className="mt-2 text-sm text-[#4A5568] max-w-2xl">
            FAQ-style entries auto-injected as context into every Chat response. Mine AI
            cites the top 3 matches inline as <code className="px-1 py-0.5 bg-[#F5EFE6] rounded text-xs">[KB1]</code>,{" "}
            <code className="px-1 py-0.5 bg-[#F5EFE6] rounded text-xs">[KB2]</code>,{" "}
            <code className="px-1 py-0.5 bg-[#F5EFE6] rounded text-xs">[KB3]</code> when relevant.
          </p>
        </div>
        <button
          data-testid="kb-new-btn"
          onClick={() => setEditing({})}
          className="px-4 py-2.5 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth flex items-center gap-2 text-sm"
        >
          <Plus size={16} weight="bold" /> New entry
        </button>
      </div>

      {/* Filters */}
      <div className="mt-5 flex items-center gap-2 flex-wrap">
        <div className="relative">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-2.5 top-2.5 text-[#4A5568]" />
          <input
            data-testid="kb-search"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search question, answer, tags..."
            className="h-9 pl-7 pr-3 rounded-md border border-[#E8DFD1] bg-white text-sm outline-none focus:border-[#3B5BDB] w-72"
          />
        </div>
        <Select
          value={category || "__all__"}
          onValueChange={(v) => setCategory(v === "__all__" ? "" : v)}
        >
          <SelectTrigger
            data-testid="kb-category-filter"
            className="h-9 w-48 rounded-md border-[#E8DFD1] bg-white text-sm focus:border-[#3B5BDB]"
          >
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All categories ({total})</SelectItem>
            {(categoriesUsed.length ? categoriesUsed : CATEGORIES).map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Entries */}
      <div className="mt-5">
        {loading ? (
          <div className="mc-card p-10 flex items-center justify-center gap-3 text-[#4A5568]">
            <CircleNotch className="animate-spin" weight="bold" /> Loading...
          </div>
        ) : entries.length === 0 ? (
          <div className="mc-card p-10 text-center" data-testid="kb-empty">
            <BookOpen size={36} weight="duotone" className="text-[#3B5BDB]/30 mx-auto" />
            <p className="mt-3 font-display text-xl text-[#0A1128]">No entries yet</p>
            <p className="mt-1 text-sm text-[#4A5568]">Add your first jewellery knowledge entry.</p>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="kb-list">
            {entries.map((e) => (
              <li
                key={e.id}
                className="mc-card p-5"
                data-testid={`kb-${e.id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-lg text-[#0A1128]">{e.question}</p>
                    <p className="mt-1.5 text-sm text-[#4A5568] whitespace-pre-line line-clamp-3">{e.answer}</p>
                    <div className="mt-2.5 flex items-center gap-2 flex-wrap text-xs">
                      <span className="px-2 py-0.5 rounded bg-[#3B5BDB]/10 text-[#3B5BDB] uppercase tracking-wider font-medium">
                        {e.category}
                      </span>
                      {(e.tags || []).map((t) => (
                        <span key={t} className="px-2 py-0.5 rounded bg-[#F5EFE6] text-[#4A5568] flex items-center gap-1">
                          <Tag size={10} weight="bold" /> {t}
                        </span>
                      ))}
                      {e.updated_at && (
                        <span className="text-[#4A5568]/70">
                          updated {new Date(e.updated_at).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      data-testid={`kb-edit-${e.id}`}
                      onClick={() => setEditing(e)}
                      className="p-2 text-[#3B5BDB] hover:bg-[#3B5BDB]/10 rounded smooth"
                      title="Edit"
                    >
                      <Pencil size={16} weight="duotone" />
                    </button>
                    <button
                      data-testid={`kb-delete-${e.id}`}
                      onClick={() => remove(e)}
                      className="p-2 text-[#4A5568] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded smooth"
                      title="Delete"
                    >
                      <Trash size={16} weight="duotone" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing !== null && (
        <KbEditor
          entry={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function KbEditor({ entry, onClose, onSaved }) {
  const isNew = !entry.id;
  const [form, setForm] = useState({
    question: entry.question || "",
    answer: entry.answer || "",
    category: entry.category || "general",
    tags: (entry.tags || []).join(", "),
    source: entry.source || "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.question.length < 4) { toast.error("Question too short"); return; }
    if (form.answer.length < 4) { toast.error("Answer too short"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      if (isNew) {
        await api.post("/admin/knowledge", payload);
        toast.success("Entry created");
      } else {
        await api.patch(`/admin/knowledge/${entry.id}`, payload);
        toast.success("Entry saved");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      data-testid="kb-editor-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white w-full max-w-2xl rounded-t-2xl sm:rounded-xl shadow-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b border-[#E8DFD1]">
          <h2 className="font-display text-2xl text-[#0A1128]">
            {isNew ? "New entry" : "Edit entry"}
          </h2>
          <button type="button" onClick={onClose} className="text-[#4A5568] hover:text-[#0A1128]">
            <X size={20} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">Question *</label>
            <input
              data-testid="kb-question"
              type="text"
              required
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">Answer (Markdown OK) *</label>
            <textarea
              data-testid="kb-answer"
              required
              rows={10}
              value={form.answer}
              onChange={(e) => setForm({ ...form, answer: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB] font-mono text-sm leading-relaxed"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">Category</label>
              <select
                data-testid="kb-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
              >
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">Tags (comma-separated)</label>
              <input
                data-testid="kb-tags"
                type="text"
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="BIS, hallmark, HUID"
                className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
              />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">Source (optional)</label>
            <input
              data-testid="kb-source"
              type="text"
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              placeholder="BIS Care app · Mine AI internal · etc."
              className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-[#E8DFD1]">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-[#4A5568] hover:text-[#0A1128] text-sm">
            Cancel
          </button>
          <button
            type="submit"
            data-testid="kb-save"
            disabled={saving}
            className="px-5 py-2.5 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth disabled:opacity-60 flex items-center gap-2"
          >
            {saving ? <CircleNotch size={14} className="animate-spin" weight="bold" /> : <CheckCircle size={14} weight="duotone" />}
            {saving ? "Saving..." : isNew ? "Create" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}
